import { useCallback, useEffect, useRef, useState } from "react";

import { useUploadStore } from "@/modules/common/store/useUploadStore";

/**
 * Generic file upload hook. The upload itself runs inside `useUploadStore`, but it is bound to the
 * lifetime of the caller: unmounting aborts the request in flight and drops the entry, so we never
 * leave a confirmed-but-unused attachment behind (nor keep the `File` in memory). Each call site
 * tracks its own `uploadId`, so independent `useFileUpload()` instances never read or affect each
 * other's state.
 */
const useFileUpload = () => {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const uploadIdRef = useRef<string | null>(null);
  const upload = useUploadStore((state) => (uploadId ? state.uploads[uploadId] : undefined));

  const trackUpload = (id: string | null) => {
    uploadIdRef.current = id;
    setUploadId(id);
  };

  const start = useCallback((file: File) => {
    const id = useUploadStore.getState().startUpload(file);
    trackUpload(id);
    return id;
  }, []);

  const cancel = useCallback(() => {
    if (uploadIdRef.current) useUploadStore.getState().cancelUpload(uploadIdRef.current);
  }, []);

  const reset = useCallback(() => {
    if (uploadIdRef.current) useUploadStore.getState().removeUpload(uploadIdRef.current);
    trackUpload(null);
  }, []);

  useEffect(
    () => () => {
      if (uploadIdRef.current) useUploadStore.getState().removeUpload(uploadIdRef.current);
    },
    [],
  );

  return { upload, start, cancel, reset };
};

export default useFileUpload;
